import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import {
	demoUsesQuery,
	unitEvcStatusQuery,
	unitStructureQuery,
} from 'api/queries';
import { AreaName } from 'domain/constants';
import { isControlSystemStatusCorrect } from 'domain/unit/EVC';
import { isDemoDealerRegisterButtonDisabled } from 'domain/unit/Unit';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { reactPlugin } from 'main/azureInsights/config';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { DemoDealerUsageView } from 'modules/UnitOverview/DemoDealerUsage/DemoDealerUsageView';
import { RegisterDemoDealerUsage } from 'modules/UnitOverview/DemoDealerUsage/RegisterDemoDealerUsage';
import { EVCStatusError } from 'modules/UnitOverview/UnitInformation/Components/EVCStatusError';

const DemoDealerUsageSectionPage = (): React.JSX.Element | null => {
	const { t } = useTranslation(['unitOverview', 'unitCreation']);
	const unitIds = useResolvedUnitIds();
	const [showRegister, setShowRegister] = useState(false);

	const theme = useTheme();

	const { api } = useProductCenterApi();
	const { data: demoUsesData } = useSuspenseQuery({
		...demoUsesQuery(api, unitIds.id),
	});

	const { data: evcStatus } = useSuspenseQuery(
		unitEvcStatusQuery(api, unitIds.id)
	);

	const { data: unitData } = useSuspenseQuery(unitStructureQuery(unitIds.id));
	const isRegisterButtonDisabled =
		isDemoDealerRegisterButtonDisabled(unitData);
	const isEVCCorrect = isControlSystemStatusCorrect(evcStatus);

	return (
		<>
			{!isEVCCorrect && <EVCStatusError />}
			{demoUsesData?.length ? (
				<DemoDealerUsageView demo={demoUsesData[0]} />
			) : (
				<AuthorizeComponent
					areaName={AreaName.DemoUse}
					permission={OperationType.Create}>
					<Box
						sx={{
							paddingTop: '1em',
						}}>
						{!showRegister && (
							<>
								<Button
									variant="contained"
									color="secondary"
									disabled={isRegisterButtonDisabled}
									onClick={() => setShowRegister(true)}>
									{t(
										'unitOverview:register-demo-usage'
									).toUpperCase()}
								</Button>

								<Typography
									sx={{
										marginTop: '1em',
										fontSize: theme.typography.body1,
									}}>
									{t(
										'unitCreation:stock-demo-exhibition-unit-text'
									)}
								</Typography>
							</>
						)}

						{showRegister && (
							<RegisterDemoDealerUsage
								id={unitIds.id}
								cancelAction={() => setShowRegister(false)}
							/>
						)}
					</Box>
				</AuthorizeComponent>
			)}
		</>
	);
};

export default withAITracking(
	reactPlugin,
	DemoDealerUsageSectionPage,
	'UnitOverviewDemoDealerUsageSectionPage',
	'application-insight-container'
);
